import React, { useEffect, useRef, useState } from "react";
import { FileSpreadsheet, Download, Upload } from "lucide-react";
import { api, getToken } from "@/lib/api";
import { toast } from "sonner";
import { Card, Loading, PageHeader, Button, Badge } from "@/components/common";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Bulk() {
  const [entities, setEntities] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/bulk/entities").then((d) => { setEntities(d); setSel(d[0] || null); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const url = (path) => `${BASE}/bulk/${sel.key}/${path}?token=${encodeURIComponent(getToken() || "")}`;

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !sel) return;
    setBusy(true); setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${BASE}/bulk/${sel.key}/import`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: form });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail?.message || data.detail || "Import gagal");
      setResult(data);
      toast.success(`Import selesai: ${data.created || 0} baru, ${data.updated || 0} update`);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (loading) return <Loading />;

  return (
    <>
      <PageHeader title="Import / Export Excel" subtitle="Kelola data massal via file .xlsx" icon={FileSpreadsheet} />
      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <Card className="p-2">
          {entities.map((e) => (
            <button key={e.key} onClick={() => { setSel(e); setResult(null); }} className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium ${sel?.key === e.key ? "bg-primary/10 text-primary" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`bulk-entity-${e.key}`}>
              {e.label}
            </button>
          ))}
        </Card>

        {sel && (
          <Card className="p-5">
            <h3 className="font-semibold text-slate-800 mb-1">{sel.label}</h3>
            <p className="text-sm text-slate-400 mb-4">{sel.supports_update ? "Mendukung tambah & update (berdasarkan SKU/kode)." : "Hanya menambah data baru."}</p>

            <div className="flex flex-wrap gap-2 mb-5">
              <a href={url("template")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 h-9 rounded-xl border border-slate-300 text-sm hover:bg-slate-50" data-testid="bulk-template"><Download size={15} /> Template Kosong</a>
              <a href={url("export")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 h-9 rounded-xl border border-slate-300 text-sm hover:bg-slate-50" data-testid="bulk-export"><Download size={15} /> Export Data</a>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={doImport} className="hidden" data-testid="bulk-file" />
              <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy}><Upload size={15} /> {busy ? "Mengunggah..." : "Import File"}</Button>
            </div>

            <div className="text-xs font-semibold uppercase text-slate-400 mb-2">Kolom</div>
            <div className="flex flex-wrap gap-2 mb-4">
              {sel.columns.map((c) => (
                <Badge key={c.key} className={c.required ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-500"}>{c.label}{c.required ? " *" : ""}</Badge>
              ))}
            </div>

            {result && (
              <div className="mt-4 p-4 rounded-xl bg-slate-50 text-sm">
                <div className="font-medium text-slate-700 mb-1">Hasil Import</div>
                <div className="text-slate-500">Baru: {result.created || 0} · Update: {result.updated || 0} · Error: {(result.errors || []).length}</div>
                {(result.errors || []).slice(0, 10).map((er, i) => <div key={i} className="text-rose-600 text-xs mt-1">Baris {er.row}: {er.error || er.message}</div>)}
              </div>
            )}
          </Card>
        )}
      </div>
    </>
  );
}
